import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { Trash2 } from "lucide-react";
import { useTheme } from "../../../../../context/ThemeContext";

import { STEP_TYPES, getNextStepOptions } from "./transportConstants";
import { createDefaultStep, validateChain, syncAdjacentSteps } from "./transportHelpers";
import { StepTypeButton } from "./StepTypeButton";
import { StepEditor } from "./StepEditor";

export const CustomTransportBuilder = ({ origin, destination, pickupTime, onOptionBuilt }) => {
  const muiTheme = useMuiTheme();
  const { isDark } = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [steps, setSteps] = useState([]);

  const nextOptions = useMemo(() => getNextStepOptions(steps), [steps]);

  /* ── Add step ──────────────────────────────────────────────────── */

  const handleAddStep = useCallback(
    (type) => {
      const newStep = createDefaultStep(type, steps, { origin, pickupTime });

      // If adding a WAIT, auto-append the following BUS stub too
      if (type === STEP_TYPES.WAIT) {
        const followingBus = createDefaultStep(STEP_TYPES.BUS, [...steps, newStep], {
          origin,
          pickupTime,
        });
        setSteps((prev) => [...prev, newStep, followingBus]);
      } else {
        setSteps((prev) => [...prev, newStep]);
      }
    },
    [steps, origin, pickupTime]
  );

  /* ── Remove step ───────────────────────────────────────────────── */

  const handleRemoveStep = useCallback((index) => {
    setSteps((prev) => validateChain(prev.filter((_, i) => i !== index)));
  }, []);

  /* ── Change step ───────────────────────────────────────────────── */

  const handleChangeStep = useCallback((index, updatedStep) => {
    setSteps((prev) => syncAdjacentSteps(prev, index, updatedStep));
  }, []);

  /* ── Reorder ───────────────────────────────────────────────────── */

  const handleMoveUp = useCallback((index) => {
    if (index === 0) return;
    setSteps((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return validateChain(next);
    });
  }, []);

  const handleMoveDown = useCallback((index) => {
    setSteps((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return validateChain(next);
    });
  }, []);

  /* ── Clear ─────────────────────────────────────────────────────── */

  const handleClearAll = useCallback(() => setSteps([]), []);

  /* ── Notify parent ─────────────────────────────────────────────── */

  React.useEffect(() => {
    if (onOptionBuilt) onOptionBuilt(steps.length > 0 ? steps : null);
  }, [steps, onOptionBuilt]);

  /* ── Render ────────────────────────────────────────────────────── */

  return (
    <Box>
      {/* Empty state */}
      {steps.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            px: 2,
            border: "2px dashed",
            borderColor: isDark ? "rgba(255,255,255,0.12)" : "grey.300",
            borderRadius: 3,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aloita lisäämällä ensimmäinen kuljetusvaihe
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            {nextOptions.map((type) => (
              <StepTypeButton key={type} type={type} onClick={handleAddStep} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Steps list */}
      {steps.length > 0 && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Kuljetusvaiheet ({steps.length})
            </Typography>
            <Button
              size="small"
              color="error"
              onClick={handleClearAll}
              startIcon={<Trash2 size={14} />}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
            >
              Tyhjennä
            </Button>
          </Stack>

          <Box
            sx={{
              pl: { xs: 0, sm: 1 },
              maxHeight: isMobile ? "45vh" : "55vh",
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {steps.map((step, index) => (
              <StepEditor
                key={`${step.type}-${index}`}
                step={step}
                index={index}
                isLast={index === steps.length - 1}
                onChange={handleChangeStep}
                onRemove={handleRemoveStep}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                totalSteps={steps.length}
              />
            ))}
          </Box>

          {/* Add next step — hidden when last step is WAIT (bus auto-appended) */}
          {nextOptions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Lisää seuraava vaihe
                </Typography>
              </Divider>
              <Stack direction="row" spacing={1} justifyContent="center">
                {nextOptions.map((type) => (
                  <StepTypeButton key={type} type={type} onClick={handleAddStep} size="small" />
                ))}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};